import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaChevronLeft, FaChevronRight, FaPlus, FaSyncAlt } from 'react-icons/fa';
import { CgSpinner } from 'react-icons/cg';
import { toast } from 'react-toastify';

import { userAppData } from '../MainApp.js';
import Header from '../common/header.js';
import PolarSwitch from '../common/formComponents/polarSwitch.js';
import {
    getSysManagerLearners,
    getLearnerSyncResults,
    getSyncErrorDetails,
    updateSysManagerLearnerFlag,
} from './sysManagerLearnersApi.js';

import './SysManagerLearners.css';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };
const EDITABLE_FIELDS = ['ALN', 'MTT', 'FSM'];
const RESULTS_PER_PAGE_OPTIONS = ['10', '25', '50', '100', 'all'];

/** Normalise one learner row so older records remain safe to display and edit. */
function normaliseLearner(rawLearner) {
    return {
        id: String(rawLearner?.id || '').trim(),
        pupilName: String(rawLearner?.pupilName || '').trim(),
        classId: String(rawLearner?.classId || '').trim(),
        groupId: String(rawLearner?.groupId || '').trim(),
        UPN: String(rawLearner?.UPN || '').trim(),
        isArchived: Number(rawLearner?.isArchived ?? 0) === 1,
        ALN: rawLearner?.ALN === true,
        MTT: rawLearner?.MTT === true,
        FSM: rawLearner?.FSM === true,
    };
}

/** Displays the learner administration landing page for authorised System Managers. */
export default function SysManagerLearners() {
    const navigate = useNavigate();
    const sysManagerPermissions = userAppData?.sysManager || {};
    const canManageLearners = Number(sysManagerPermissions.perms ?? 0) === 1
        && sysManagerPermissions.learnerAdmin === true;
    const [learners, setLearners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [includeArchived, setIncludeArchived] = useState(false);
    const [groupFilter, setGroupFilter] = useState('all');
    const [classFilter, setClassFilter] = useState('all');
    const [flagFilters, setFlagFilters] = useState({ ALN: 'all', MTT: 'all', FSM: 'all' });
    const [sortField, setSortField] = useState('pupilName');
    const [sortDirection, setSortDirection] = useState('asc');
    const [resultsPerPage, setResultsPerPage] = useState('25');
    const [currentPage, setCurrentPage] = useState(1);
    const [updatingFields, setUpdatingFields] = useState(() => new Set());
    const [syncResultsEnabled, setSyncResultsEnabled] = useState(false);
    const [syncResultsOpen, setSyncResultsOpen] = useState(false);
    const [syncResults, setSyncResults] = useState([]);
    const [syncResultsLoading, setSyncResultsLoading] = useState(false);
    const [syncResultsFailed, setSyncResultsFailed] = useState(false);
    const [syncErrorsOpen, setSyncErrorsOpen] = useState(false);
    const [syncErrors, setSyncErrors] = useState([]);
    const [syncErrorsLoading, setSyncErrorsLoading] = useState(false);
    const [syncErrorsFailed, setSyncErrorsFailed] = useState(false);
    const [selectedSyncRun, setSelectedSyncRun] = useState(null);

    const groupOptions = useMemo(() => (
        [...new Set(learners.map((learner) => learner.groupId).filter(Boolean))]
            .sort((firstValue, secondValue) => firstValue.localeCompare(secondValue, undefined, { numeric: true }))
    ), [learners]);

    const classOptions = useMemo(() => (
        [...new Set(learners
            .filter((learner) => groupFilter === 'all' || learner.groupId === groupFilter)
            .map((learner) => learner.classId)
            .filter(Boolean))]
            .sort((firstValue, secondValue) => firstValue.localeCompare(secondValue, undefined, { numeric: true }))
    ), [groupFilter, learners]);

    const filteredAndSortedLearners = useMemo(() => {
        const cleanSearchText = searchText.trim().toLowerCase();
        const filteredResults = learners.filter((learner) => {
            const matchesSearch = !cleanSearchText || [
                learner.pupilName,
                learner.classId,
                learner.groupId,
                learner.id,
                learner.UPN,
            ]
                .join(' ')
                .toLowerCase()
                .includes(cleanSearchText);
            const matchesGroup = groupFilter === 'all' || learner.groupId === groupFilter;
            const matchesClass = classFilter === 'all' || learner.classId === classFilter;
            const matchesFlags = EDITABLE_FIELDS.every((fieldName) => (
                flagFilters[fieldName] === 'all'
                || learner[fieldName] === (flagFilters[fieldName] === 'yes')
            ));

            return matchesSearch && matchesGroup && matchesClass && matchesFlags;
        });

        return [...filteredResults].sort((firstLearner, secondLearner) => {
            const firstValue = String(firstLearner[sortField] || '');
            const secondValue = String(secondLearner[sortField] || '');
            const comparison = firstValue.localeCompare(secondValue, undefined, {
                numeric: true,
                sensitivity: 'base',
            });
            return sortDirection === 'desc' ? -comparison : comparison;
        });
    }, [classFilter, flagFilters, groupFilter, learners, searchText, sortDirection, sortField]);

    const pageSize = resultsPerPage === 'all'
        ? Math.max(filteredAndSortedLearners.length, 1)
        : Number(resultsPerPage);
    const totalPages = Math.max(1, Math.ceil(filteredAndSortedLearners.length / pageSize));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const pageStartIndex = (safeCurrentPage - 1) * pageSize;
    const visibleLearners = filteredAndSortedLearners.slice(pageStartIndex, pageStartIndex + pageSize);
    const firstVisibleResult = filteredAndSortedLearners.length === 0 ? 0 : pageStartIndex + 1;
    const lastVisibleResult = Math.min(pageStartIndex + visibleLearners.length, filteredAndSortedLearners.length);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    /** Load active learners when the authorised administration page opens. */
    async function loadLearners(includeArchivedValue = includeArchived) {
        setLoading(true);
        setLoadFailed(false);
        try {
            const response = await getSysManagerLearners(includeArchivedValue);
            const normalisedLearners = response.items
                .map(normaliseLearner)
                .filter((learner) => learner.id && learner.pupilName);
            setLearners(normalisedLearners);
        } catch (error) {
            console.error('Failed to load System Manager learners', error);
            setLoadFailed(true);
            setLearners([]);
            toast.error('❌ Failed to load learners.', TOAST_CONFIG);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (canManageLearners) {
            setCurrentPage(1);
            loadLearners(includeArchived);
        }
    }, [canManageLearners, includeArchived]);

    useEffect(() => {
        let isCurrent = true;

        /** Keep the Wonde-only action hidden unless the school configuration enables it. */
        async function loadSyncAvailability() {
            try {
                const response = await getLearnerSyncResults(true);
                if (isCurrent) setSyncResultsEnabled(response.enabled === true);
            } catch (error) {
                console.error('Failed to check Wonde learner sync availability', error);
                if (isCurrent) setSyncResultsEnabled(false);
            }
        }

        if (canManageLearners) loadSyncAvailability();
        return () => { isCurrent = false; };
    }, [canManageLearners]);

    /** Open the results dialog and fetch the latest five runs from the audit table. */
    async function openSyncResults() {
        setSyncResultsOpen(true);
        setSyncResultsLoading(true);
        setSyncResultsFailed(false);
        try {
            const response = await getLearnerSyncResults(false);
            if (!response.enabled) {
                setSyncResultsEnabled(false);
                setSyncResultsOpen(false);
                return;
            }
            setSyncResults(response.items);
        } catch (error) {
            console.error('Failed to load Wonde learner sync results', error);
            setSyncResultsFailed(true);
            toast.error('❌ Failed to load sync results.', TOAST_CONFIG);
        } finally {
            setSyncResultsLoading(false);
        }
    }

    /** Load the safe audit details for one run and display them in a modal. */
    async function openSyncErrors(result) {
        if (!result?.logId || Number(result.errors || 0) < 1) return;
        setSelectedSyncRun(result);
        setSyncErrorsOpen(true);
        setSyncErrorsLoading(true);
        setSyncErrorsFailed(false);
        setSyncErrors([]);
        try {
            const response = await getSyncErrorDetails(result.logId);
            setSyncErrors(response.items);
        } catch (error) {
            console.error('Failed to load Wonde sync error details', error);
            setSyncErrorsFailed(true);
            toast.error('❌ Failed to load sync errors.', TOAST_CONFIG);
        } finally {
            setSyncErrorsLoading(false);
        }
    }

    /** Convert stored audit action codes into concise administrator wording. */
    function formatSyncErrorAction(action) {
        const labels = {
            ERROR: 'Error',
            SKIPPED_DUPLICATE_EMAIL: 'Duplicate email',
            SKIPPED_MISSING_EMAIL: 'Missing email',
        };
        return labels[action] || String(action || 'Error').replaceAll('_', ' ');
    }

    /** Format an ISO timestamp for the administrator's local timezone. */
    function formatSyncDate(value) {
        const parsedDate = new Date(value);
        return Number.isNaN(parsedDate.getTime()) ? 'Not available' : parsedDate.toLocaleString();
    }

    /** Reset every learner filter without changing the selected sort or page size. */
    function clearFilters() {
        setSearchText('');
        setIncludeArchived(false);
        setGroupFilter('all');
        setClassFilter('all');
        setFlagFilters({ ALN: 'all', MTT: 'all', FSM: 'all' });
        setCurrentPage(1);
    }

    const filtersAreClear = !searchText
        && !includeArchived
        && groupFilter === 'all'
        && classFilter === 'all'
        && EDITABLE_FIELDS.every((fieldName) => flagFilters[fieldName] === 'all');

    /** Persist one learner support flag while locking only the affected switch. */
    async function handleFlagChange(learner, fieldName, nextValue) {
        if (!EDITABLE_FIELDS.includes(fieldName)) {
            return;
        }

        const updateKey = `${learner.id}-${fieldName}`;
        if (updatingFields.has(updateKey)) {
            return;
        }

        setUpdatingFields((currentFields) => new Set(currentFields).add(updateKey));
        try {
            const response = await updateSysManagerLearnerFlag({
                pupilId: learner.id,
                field: fieldName,
                value: nextValue,
            });
            setLearners((currentLearners) => currentLearners.map((currentLearner) => (
                currentLearner.id === response.pupilId
                    ? { ...currentLearner, [response.field]: response.value }
                    : currentLearner
            )));
            toast.success(`✅ Updated ${learner.pupilName}.`, TOAST_CONFIG);
        } catch (error) {
            console.error('Failed to update learner support flag', error);
            toast.error(`❌ Failed to update ${learner.pupilName}.`, TOAST_CONFIG);
        } finally {
            setUpdatingFields((currentFields) => {
                const nextFields = new Set(currentFields);
                nextFields.delete(updateKey);
                return nextFields;
            });
        }
    }

    if (!canManageLearners) {
        return <Navigate to='/app' replace />;
    }

    return (
        <div className='main_div'>
            <div className='internal_main_div sysManager_learners_container'>
                <Header
                    title='Learners'
                    subtitle='Review active learners and maintain their ALN, MTT and FSM indicators.'
                    rightContent={<>
                        <button
                            className='module-page-header__button'
                            type='button'
                            onClick={() => navigate('/app/sysmanager')}
                        >
                            <FaArrowLeft aria-hidden='true' />
                            Back to System Manager
                        </button>
                    </>}
                />

                <section className='sysManager_learners_actionsSurface' aria-label='Learner management actions'>
                    {syncResultsEnabled && (
                        <button
                            className='sysManager_learners_actionButton'
                            type='button'
                            onClick={openSyncResults}
                        >
                            <FaSyncAlt aria-hidden='true' />
                            Sync Results
                        </button>
                    )}
                    <button
                        className='sysManager_learners_actionButton'
                        type='button'
                        onClick={() => navigate('/app/sysmanager/learners/new')}
                    >
                        <FaPlus aria-hidden='true' />
                        Add new learner
                    </button>
                </section>

                {syncResultsOpen && (
                    <div className='sysManager_syncResults_overlay' role='presentation' onMouseDown={(event) => {
                        if (event.target === event.currentTarget) setSyncResultsOpen(false);
                    }}>
                        <section className='sysManager_syncResults_modal' role='dialog' aria-modal='true' aria-labelledby='sync-results-title'>
                            <button
                                className='sysManager_syncResults_close'
                                type='button'
                                aria-label='Close sync results'
                                onClick={() => setSyncResultsOpen(false)}
                            >×</button>
                            <div className='sysManager_syncResults_header'>
                                <h2 id='sync-results-title'>Wonde learner sync results</h2>
                                <p>The five most recent learner synchronisations.</p>
                            </div>

                            {syncResultsLoading && (
                                <div className='sysManager_syncResults_status'>
                                    <CgSpinner className='sysManager_learners_spinner' />
                                    <p>Loading sync results...</p>
                                </div>
                            )}

                            {!syncResultsLoading && syncResultsFailed && (
                                <div className='sysManager_syncResults_status'>
                                    <p>Sync results could not be loaded.</p>
                                    <button type='button' onClick={openSyncResults}>Try again</button>
                                </div>
                            )}

                            {!syncResultsLoading && !syncResultsFailed && syncResults.length === 0 && (
                                <div className='sysManager_syncResults_status'>
                                    <p>No learner syncs have been recorded yet.</p>
                                </div>
                            )}

                            {!syncResultsLoading && !syncResultsFailed && syncResults.length > 0 && (
                                <div className='sysManager_syncResults_tableWrap'>
                                    <table className='sysManager_syncResults_table'>
                                        <thead><tr><th>Run</th><th>Status</th><th>Added</th><th>Archived</th><th>Updated</th><th>Errors</th></tr></thead>
                                        <tbody>
                                            {syncResults.map((result) => (
                                                <tr key={result.logId}>
                                                    <td data-label='Run'>{formatSyncDate(result.startedAt)}</td>
                                                    <td data-label='Status'>{result.status}</td>
                                                    <td data-label='Added'>{result.added}</td>
                                                    <td data-label='Archived'>{result.archived}</td>
                                                    <td data-label='Updated'>{result.updated}</td>
                                                    <td data-label='Errors'>
                                                        {Number(result.errors || 0) > 0 ? (
                                                            <button
                                                                className='sysManager_syncResults_errorButton'
                                                                type='button'
                                                                onClick={() => openSyncErrors(result)}
                                                                aria-label={`View ${result.errors} sync errors`}
                                                            >
                                                                {result.errors}
                                                            </button>
                                                        ) : 0}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    </div>
                )}

                {syncErrorsOpen && (
                    <div className='sysManager_syncErrors_overlay' role='presentation' onMouseDown={(event) => {
                        if (event.target === event.currentTarget) setSyncErrorsOpen(false);
                    }}>
                        <section className='sysManager_syncErrors_modal' role='dialog' aria-modal='true' aria-labelledby='sync-errors-title'>
                            <button
                                className='sysManager_syncResults_close'
                                type='button'
                                aria-label='Close sync errors'
                                onClick={() => setSyncErrorsOpen(false)}
                            >×</button>
                            <div className='sysManager_syncResults_header'>
                                <h2 id='sync-errors-title'>Sync errors</h2>
                                <p>
                                    Learner run from{' '}
                                    {formatSyncDate(selectedSyncRun?.startedAt)}
                                </p>
                            </div>

                            {syncErrorsLoading && (
                                <div className='sysManager_syncResults_status'>
                                    <CgSpinner className='sysManager_learners_spinner' />
                                    <p>Loading sync errors...</p>
                                </div>
                            )}

                            {!syncErrorsLoading && syncErrorsFailed && (
                                <div className='sysManager_syncResults_status'>
                                    <p>Sync errors could not be loaded.</p>
                                    <button type='button' onClick={() => openSyncErrors(selectedSyncRun)}>Try again</button>
                                </div>
                            )}

                            {!syncErrorsLoading && !syncErrorsFailed && syncErrors.length === 0 && (
                                <div className='sysManager_syncResults_status'>
                                    <p>No stored error details were found for this run.</p>
                                </div>
                            )}

                            {!syncErrorsLoading && !syncErrorsFailed && syncErrors.length > 0 && (
                                <div className='sysManager_syncErrors_list'>
                                    {syncErrors.map((error, index) => (
                                        <article className='sysManager_syncErrors_item' key={`${error.action}-${error.wondeId || error.entityId}-${index}`}>
                                            <div>
                                                <strong>{formatSyncErrorAction(error.action)}</strong>
                                                <span>{error.displayName || 'Unknown user'}</span>
                                            </div>
                                            {error.message && <p>{error.message}</p>}
                                            <dl>
                                                {error.email && <><dt>Email</dt><dd>{error.email}</dd></>}
                                                {error.staffCode && <><dt>Staff code</dt><dd>{error.staffCode}</dd></>}
                                                {error.UPN && <><dt>UPN</dt><dd>{error.UPN}</dd></>}
                                                {error.wondeId && <><dt>Wonde ID</dt><dd>{error.wondeId}</dd></>}
                                                {error.errorCode && <><dt>Error code</dt><dd>{error.errorCode}</dd></>}
                                            </dl>
                                        </article>
                                    ))}
                                </div>
                            )}
                        </section>
                    </div>
                )}

                <section className='sysManager_learners_card'>
                    <div className='sysManager_learners_toolbar'>
                        <div>
                            <h2>Learners</h2>
                            <p>
                                Showing {firstVisibleResult}-{lastVisibleResult} of {filteredAndSortedLearners.length}
                                {filteredAndSortedLearners.length !== learners.length ? ` filtered from ${learners.length}` : ''} learners
                            </p>
                        </div>
                        <input
                            aria-label='Search learners'
                            type='search'
                            value={searchText}
                            onChange={(event) => {
                                setSearchText(event.target.value);
                                setCurrentPage(1);
                            }}
                            placeholder='Search name, class, ID or UPN'
                        />
                    </div>

                    {loading && (
                        <div className='sysManager_learners_status'>
                            <CgSpinner className='sysManager_learners_spinner' />
                            <p>Loading learners...</p>
                        </div>
                    )}

                    {!loading && loadFailed && (
                        <div className='sysManager_learners_status'>
                            <p>Learners could not be loaded.</p>
                            <button type='button' onClick={() => loadLearners(includeArchived)}>Try again</button>
                        </div>
                    )}

                    {!loading && !loadFailed && filteredAndSortedLearners.length === 0 && (
                        <div className='sysManager_learners_status'>
                            <p>{learners.length === 0 ? 'No learners were found.' : 'No learners match your filters.'}</p>
                        </div>
                    )}

                    {!loading && !loadFailed && learners.length > 0 && (
                        <div className='sysManager_learners_contentGrid'>
                            <div className='sysManager_learners_mainContent'>
                            {visibleLearners.length > 0 && <div className='sysManager_learners_tableWrap'>
                                <table className='sysManager_learners_table'>
                                <thead>
                                    <tr>
                                        <th>Learner</th>
                                        <th>Year group</th>
                                        <th>Class</th>
                                        <th>UPN</th>
                                        <th>ALN</th>
                                        <th>MTT</th>
                                        <th>FSM</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleLearners.map((learner) => (
                                        <tr key={learner.id}>
                                            <td data-label='Learner'>
                                                <strong>
                                                    {learner.pupilName}
                                                    {learner.isArchived && <span className='sysManager_learners_archivedBadge'>Archived</span>}
                                                </strong>
                                            </td>
                                            <td data-label='Year group'>{learner.groupId || 'Not set'}</td>
                                            <td data-label='Class'>{learner.classId || 'Not set'}</td>
                                            <td data-label='UPN'>{learner.UPN || 'Not set'}</td>
                                            {EDITABLE_FIELDS.map((fieldName) => {
                                                const updateKey = `${learner.id}-${fieldName}`;
                                                const isUpdating = updatingFields.has(updateKey);
                                                return (
                                                    <td
                                                        data-label={fieldName}
                                                        key={fieldName}
                                                        title={learner.isArchived ? 'Archived learners cannot be edited.' : undefined}
                                                    >
                                                        <PolarSwitch
                                                            ariaLabel={`${fieldName} for ${learner.pupilName}`}
                                                            data={learner[fieldName]}
                                                            loading={isUpdating}
                                                            disabled={isUpdating || learner.isArchived}
                                                            setData={(nextValue) => handleFlagChange(learner, fieldName, nextValue)}
                                                        />
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                                </table>
                            </div>}

                            {filteredAndSortedLearners.length > 0 && (
                                <nav className='sysManager_learners_pagination' aria-label='Learner table pages'>
                                    <button
                                        type='button'
                                        aria-label='Previous page'
                                        disabled={safeCurrentPage === 1}
                                        onClick={() => setCurrentPage((pageNumber) => Math.max(1, pageNumber - 1))}
                                    >
                                        <FaChevronLeft aria-hidden='true' />
                                    </button>
                                    <span>Page {safeCurrentPage} of {totalPages}</span>
                                    <button
                                        type='button'
                                        aria-label='Next page'
                                        disabled={safeCurrentPage === totalPages}
                                        onClick={() => setCurrentPage((pageNumber) => Math.min(totalPages, pageNumber + 1))}
                                    >
                                        <FaChevronRight aria-hidden='true' />
                                    </button>
                                </nav>
                            )}
                            </div>

                            <aside className='sysManager_learners_sidebar' aria-label='Learner filters and sorting'>
                                <section className='sysManager_learners_controlPanel'>
                                    <h3>Filter learners</h3>
                                    <label className='sysManager_learners_checkboxField'>
                                        <input
                                            type='checkbox'
                                            checked={includeArchived}
                                            onChange={(event) => setIncludeArchived(event.target.checked)}
                                        />
                                        <span>Include archived learners</span>
                                    </label>
                                    <label>
                                        <span>Year group</span>
                                        <select
                                            value={groupFilter}
                                            onChange={(event) => {
                                                setGroupFilter(event.target.value);
                                                setClassFilter('all');
                                                setCurrentPage(1);
                                            }}
                                        >
                                            <option value='all'>All year groups</option>
                                            {groupOptions.map((groupName) => <option value={groupName} key={groupName}>{groupName}</option>)}
                                        </select>
                                    </label>
                                    <label>
                                        <span>Class</span>
                                        <select value={classFilter} onChange={(event) => { setClassFilter(event.target.value); setCurrentPage(1); }}>
                                            <option value='all'>All classes</option>
                                            {classOptions.map((className) => <option value={className} key={className}>{className}</option>)}
                                        </select>
                                    </label>
                                    {EDITABLE_FIELDS.map((fieldName) => (
                                        <label key={fieldName}>
                                            <span>{fieldName}</span>
                                            <select
                                                value={flagFilters[fieldName]}
                                                onChange={(event) => {
                                                    setFlagFilters((currentFilters) => ({ ...currentFilters, [fieldName]: event.target.value }));
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                <option value='all'>All</option>
                                                <option value='yes'>Yes</option>
                                                <option value='no'>No</option>
                                            </select>
                                        </label>
                                    ))}
                                    <button type='button' className='sysManager_learners_clearButton' onClick={clearFilters} disabled={filtersAreClear}>
                                        Clear filters
                                    </button>
                                </section>

                                <section className='sysManager_learners_controlPanel'>
                                    <h3>Sort learners</h3>
                                    <label>
                                        <span>Sort by</span>
                                        <select value={sortField} onChange={(event) => { setSortField(event.target.value); setCurrentPage(1); }}>
                                            <option value='pupilName'>Learner name</option>
                                            <option value='groupId'>Year group</option>
                                            <option value='classId'>Class</option>
                                            <option value='UPN'>UPN</option>
                                        </select>
                                    </label>
                                    <label>
                                        <span>Sort order</span>
                                        <select value={sortDirection} onChange={(event) => { setSortDirection(event.target.value); setCurrentPage(1); }}>
                                            <option value='asc'>A-Z / Low to high</option>
                                            <option value='desc'>Z-A / High to low</option>
                                        </select>
                                    </label>
                                    <label>
                                        <span>Results per page</span>
                                        <select value={resultsPerPage} onChange={(event) => { setResultsPerPage(event.target.value); setCurrentPage(1); }}>
                                            {RESULTS_PER_PAGE_OPTIONS.map((pageOption) => (
                                                <option value={pageOption} key={pageOption}>{pageOption === 'all' ? 'All' : pageOption}</option>
                                            ))}
                                        </select>
                                    </label>
                                </section>
                            </aside>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}
